'use client';

import dynamic from 'next/dynamic';
import { ReactNode } from 'react';
import { visualizationRegistry } from '@/components/visualizations';
import { MermaidToolOutput } from '@/components/visualizations/mermaid-renderer';
import { Molecule3DToolOutput } from '@/components/visualizations/molecule3d-renderer';
import { P5ToolOutput } from '@/components/visualizations/p5-renderer';
import { RemotionToolOutput } from '@/components/visualizations/remotion-renderer';
import { HyperframesToolOutput } from '@/components/visualizations/hyperframes-renderer';
import { GeneratedImageToolOutput } from '@/components/visualizations/generated-image-renderer';
import { InlineHtmlBlock } from '@/components/inline-html-block';
import { GeneratedImageBatchToolOutput } from '@/components/visualizations/generated-image-batch-renderer';
import { GeneratedVideoToolOutput } from '@/components/visualizations/generated-video-renderer';
import { PendingNarratedVideo } from '@/components/visualizations/pending-narrated-video';
import { VisualizationLoader } from '@/components/visualizations/visualization-loader';
import { ShareButton } from '@/components/share-button';
import { GalexiaLogo } from '@/components/galexia-logo';
import { BrowserLiveViewRenderer } from '@/components/browser-live-view-renderer';
import { BrowserLiveView } from '@/components/sandbox/browser-live-view';
import { SandboxPreviewRenderer } from '@/components/sandbox-preview-renderer';
import { MediaArtifactRenderer } from '@/components/media-artifact-renderer';
import { DevToolArtifactRenderer } from '@/components/dev-tool-artifact-renderer';
import { DocumentArtifactRenderer } from '@/components/document-artifact-renderer';
import { resolveDelegatedToolDisplay } from '@/lib/capabilities/display';
import { DEFAULT_VIDEO_MODEL_ID } from '@/lib/media-video';
import { Skeleton } from '@/components/ai-elements/skeleton';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Printer, Copy, Check } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';

/**
 * Shared pending card for in-flight media generation (image / video /
 * narrated video). Breathing brand mark + shimmer skeleton where the media
 * will land, so the wait reads as progress instead of a stuck spinner.
 */
function MediaPendingCard({
  title,
  meta,
  prompt,
  aspect = 'video',
}: {
  title: string;
  meta?: string;
  prompt?: string;
  aspect?: 'video' | 'image' | 'none';
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-violet-500/15 bg-violet-500/[0.04] p-5">
      <div className="flex items-center gap-3">
        <span className="galexia-logo-thinking inline-flex size-7 items-center justify-center">
          <GalexiaLogo size={28} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-violet-100/95">{title}</p>
          {meta && (
            <p className="text-[11px] uppercase tracking-[2px] text-violet-300/55 mt-0.5">{meta}</p>
          )}
        </div>
      </div>
      {aspect !== 'none' && (
        <Skeleton
          className={cn(
            'mt-4 w-full',
            aspect === 'video' ? 'aspect-video max-h-56' : 'aspect-[4/3] max-h-64',
          )}
        />
      )}
      {prompt && (
        <p className="mt-3 text-xs text-muted-foreground/70 italic line-clamp-3 leading-relaxed">
          &ldquo;{prompt}&rdquo;
        </p>
      )}
    </div>
  );
}

export type VisualizationPresentationMode = 'inline' | 'artifact-preferred' | 'artifact-only';

const DEFAULT_PRESENTATION_BY_TOOL: Record<string, VisualizationPresentationMode> = {
  html: 'inline',
  htmlNative: 'artifact-preferred',
  svg: 'inline',
  mermaid: 'inline',
  p5: 'artifact-preferred',
  remotion: 'artifact-preferred',
  hyperframes: 'artifact-preferred',
  createSceneVideo: 'artifact-preferred',
  scene3d: 'artifact-preferred',
  molecule3d: 'artifact-preferred',
  // Long-running async renders: route the live status to the side panel
  // by default. The chat surface shows a compact "open in workspace"
  // chip; the panel hosts the full PendingNarratedVideo card with
  // progress bar, stage label, and the final video player on COMPLETED.
  createNarratedVideo: 'artifact-preferred',
  createParallelNarratedVideo: 'artifact-preferred',
  createBrowser: 'artifact-preferred',
  browserLiveView: 'artifact-preferred',
  browserNavigate: 'artifact-preferred',
  browserScroll: 'artifact-preferred',
  sandboxPreviewUrl: 'artifact-preferred',
  generateSpeech: 'artifact-preferred',
  generateNarrationPlan: 'artifact-preferred',
  generateMusic: 'artifact-preferred',
  generateSoundEffect: 'artifact-preferred',
  generateSoundEffectBatch: 'artifact-preferred',
  viewMediaAsset: 'artifact-preferred',
  saveMediaAssetFromUrl: 'artifact-preferred',
  promoteGeneratedMediaToAsset: 'artifact-preferred',
  sandboxExec: 'artifact-preferred',
  sandboxCodeRun: 'artifact-preferred',
  sandboxSessionExec: 'artifact-preferred',
  sandboxSessionLogs: 'artifact-preferred',
  sandboxListFiles: 'artifact-preferred',
  sandboxReadFile: 'artifact-preferred',
  browserListFiles: 'artifact-preferred',
  browserReadFile: 'artifact-preferred',
  readGalexiaFile: 'artifact-preferred',
  readGalexiaFileLines: 'artifact-preferred',
  listMyDocuments: 'artifact-preferred',
  viewDocument: 'artifact-preferred',
  getVisualizationSource: 'artifact-preferred',
  readVisualizationLines: 'artifact-preferred',
  grepVisualization: 'artifact-preferred',
};

const MEDIA_ARTIFACT_TOOLS = new Set([
  'generateSpeech',
  'generateNarrationPlan',
  'generateMusic',
  'generateSoundEffect',
  'generateSoundEffectBatch',
  'viewMediaAsset',
  'saveMediaAssetFromUrl',
  'promoteGeneratedMediaToAsset',
]);

const DEV_ARTIFACT_TOOLS = new Set([
  'sandboxExec',
  'sandboxCodeRun',
  'sandboxSessionExec',
  'sandboxSessionLogs',
  'sandboxListFiles',
  'sandboxReadFile',
  'browserListFiles',
  'browserReadFile',
  'readGalexiaFile',
  'readGalexiaFileLines',
  'getVisualizationSource',
  'readVisualizationLines',
  'grepVisualization',
]);

const DOCUMENT_ARTIFACT_TOOLS = new Set([
  'listMyDocuments',
  'viewDocument',
]);

function openNativeHtmlPrintWindow({ title, html }: { title?: string; html: string }) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  win.document.open();
  win.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${title ? title.replace(/[<>]/g, '') : 'HTML artifact'}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; padding: 24px; font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #111827; }
  img, svg, canvas, video { max-width: 100%; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>${html}</body>
</html>`);
  win.document.close();
  win.addEventListener('load', () => {
    setTimeout(() => {
      win.focus();
      win.print();
    }, 150);
  });
}

/**
 * Extract the raw authoring source for code-based artifacts so viewers can
 * offer a "Copy source" affordance. Returns a `{ source, language }` pair, or
 * null for artifact types that have no meaningful text source (images, etc.).
 */
function getArtifactSource(
  toolName: string,
  input: Record<string, unknown> | undefined,
): { source: string; language: string } | null {
  if (!input) return null;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : '');
  switch (toolName) {
    case 'mermaid':
      return str(input.code) ? { source: input.code as string, language: 'mermaid' } : null;
    case 'svg':
      return str(input.svg) ? { source: input.svg as string, language: 'svg' } : null;
    case 'html':
    case 'htmlNative':
      return str(input.html) ? { source: input.html as string, language: 'html' } : null;
    case 'p5': {
      const parts = [input.setup, input.draw, input.mousePressed, input.keyPressed]
        .filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
      return parts.length ? { source: parts.join('\n\n'), language: 'javascript' } : null;
    }
    default:
      return null;
  }
}

function CopySourceButton({ source }: { source: string }) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
  }, []);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(source);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (e.g. insecure context) — fail silently.
    }
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      className="h-7 shrink-0 gap-1.5 px-2"
      title="Copy source"
      aria-label={copied ? 'Source copied' : 'Copy source'}
    >
      {copied ? (
        <Check weight="bold" className="size-3.5 text-emerald-400" />
      ) : (
        <Copy weight="bold" className="size-3.5" />
      )}
      <span className="hidden text-xs md:inline">{copied ? 'Copied' : 'Copy'}</span>
    </Button>
  );
}

function NativeHtmlPrintButton({ title, html }: { title?: string; html: string }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => openNativeHtmlPrintWindow({ title, html })}
      className="h-7 shrink-0 gap-1.5 px-2"
      title="Print or save as PDF"
    >
      <Printer weight="bold" className="size-3.5" />
      <span className="hidden text-xs md:inline">Print</span>
    </Button>
  );
}

const Scene3DToolOutput = dynamic(
  () => import('@/components/visualizations/scene3d-renderer').then(m => ({ default: m.Scene3DToolOutput })),
  {
    ssr: false,
    loading: () => (
      <VisualizationLoader kind="3d" title="Loading 3D scene" className="h-[420px]" />
    ),
  }
);

function getPresentationValue(input: Record<string, unknown> | undefined): unknown {
  if (!input) return undefined;
  if (typeof input.presentation === 'string') return input.presentation;
  if (
    input.presentation &&
    typeof input.presentation === 'object' &&
    'mode' in input.presentation &&
    typeof (input.presentation as { mode?: unknown }).mode === 'string'
  ) {
    return (input.presentation as { mode: string }).mode;
  }
  return undefined;
}

export function resolveVisualizationPresentationMode(
  toolName: string,
  input?: Record<string, unknown>,
): VisualizationPresentationMode {
  const rawValue = getPresentationValue(input);

  if (rawValue === 'inline' || rawValue === 'artifact-preferred' || rawValue === 'artifact-only') {
    return rawValue;
  }

  // A live browser-run belongs in the artifact panel (like Kernel live view);
  // a plain scripted run stays inline as a compact result.
  if (toolName === 'sandboxBrowserRun') {
    return input?.liveView === true ? 'artifact-preferred' : 'inline';
  }

  return DEFAULT_PRESENTATION_BY_TOOL[toolName] ?? 'inline';
}

/** True when a browser-run result carries a live-view ws URL to render. */
function hasSandboxLiveView(output: unknown): boolean {
  if (!output || typeof output !== 'object') return false;
  const out = output as { liveView?: { url?: unknown }; data?: { liveView?: { url?: unknown } } };
  const url = out.liveView?.url ?? out.data?.liveView?.url;
  return typeof url === 'string' && url.startsWith('ws');
}

export function getVisualizationDisplayTitle(
  toolName: string,
  input?: Record<string, unknown>,
): string {
  const title = input?.title;
  if (typeof title === 'string' && title.trim()) {
    return title.trim();
  }

  switch (toolName) {
    case 'html':
      return 'HTML Visualization';
    case 'svg':
      return 'SVG Visualization';
    case 'p5':
      return 'Interactive Animation';
    case 'remotion':
      return 'Animated Video';
    case 'hyperframes':
      return 'HyperFrames Video';
    case 'createSceneVideo':
      return 'Scene Video';
    case 'molecule3d':
      return '3D Visualization';
    case 'scene3d':
      return '3D Scene';
    case 'mermaid':
      return 'Mermaid Diagram';
    case 'generateImage':
      return 'Generated Image';
    case 'generateSpeech':
      return 'Generated Voiceover';
    case 'generateNarrationPlan':
      return 'Narration Tracks';
    case 'generateMusic':
      return 'Generated Music';
    case 'generateSoundEffect':
    case 'generateSoundEffectBatch':
      return 'Generated Audio';
    case 'viewMediaAsset':
    case 'saveMediaAssetFromUrl':
    case 'promoteGeneratedMediaToAsset':
      return 'Media Asset';
    case 'htmlNative':
      return 'HTML (native)';
    case 'generateImageBatch':
      return 'Generated Images';
    case 'generateVideoClip':
      return 'Generated Video';
    case 'extractVideoClip':
      return 'Video Clip';
    case 'captionVideoClip':
      return 'Captioned Clip';
    case 'createVideoRecut':
      return 'Video Recut';
    case 'listMyDocuments':
      return 'Documents';
    case 'viewDocument':
      return 'Document';
    case 'getVisualizationSource':
    case 'readVisualizationLines':
    case 'grepVisualization':
      return 'Source';
    case 'createNarratedVideo':
    case 'createParallelNarratedVideo':
      return 'Narrated Video';
    case 'createBrowser':
    case 'browserLiveView':
    case 'browserNavigate':
    case 'browserScroll':
    case 'sandboxBrowserRun':
      return 'Browser Live View';
    case 'sandboxPreviewUrl':
      return 'Live Preview';
    case 'sandboxExec':
    case 'sandboxCodeRun':
    case 'sandboxSessionExec':
    case 'sandboxSessionLogs':
      return 'Terminal';
    case 'sandboxListFiles':
    case 'browserListFiles':
      return 'Files';
    case 'sandboxReadFile':
    case 'browserReadFile':
    case 'readGalexiaFile':
    case 'readGalexiaFileLines':
      return 'File';
    default:
      return toolName;
  }
}

interface ToolVisualizationRendererProps {
  toolName: string;
  input: Record<string, unknown>;
  output?: unknown;
  visualizationId?: string;
  sessionId?: string | null;
  onRetryError?: (info: { toolName: string; error: string; code: string }) => void;
  className?: string;
  displayContext?: 'inline' | 'artifact';
  renderActions?: ReactNode;
}

export function ToolVisualizationRenderer({
  toolName,
  input,
  output,
  visualizationId,
  sessionId,
  onRetryError,
  className,
  displayContext = 'inline',
  renderActions,
}: ToolVisualizationRendererProps) {
  const delegated = resolveDelegatedToolDisplay(toolName, input, output);
  if (delegated.toolName !== toolName) {
    return (
      <ToolVisualizationRenderer
        toolName={delegated.toolName}
        input={delegated.input ?? input}
        output={delegated.output ?? output}
        visualizationId={visualizationId}
        sessionId={sessionId}
        onRetryError={onRetryError}
        className={className}
        displayContext={displayContext}
        renderActions={renderActions}
      />
    );
  }

  const vizTitle = getVisualizationDisplayTitle(toolName, input);
  const shareAction = visualizationId || sessionId ? (
    <ShareButton
      visualizationId={visualizationId}
      sessionId={sessionId}
      type={toolName}
      title={vizTitle}
    />
  ) : null;

  const artifactSource = getArtifactSource(toolName, input);
  const copyAction = artifactSource ? (
    <CopySourceButton source={artifactSource.source} />
  ) : null;

  const combinedActions = displayContext === 'artifact'
    ? (
      <>
        {copyAction}
        {shareAction}
        {renderActions}
      </>
    )
    : (
      <>
        {copyAction}
        {renderActions}
        {shareAction}
      </>
    );

  if (toolName === 'mermaid') {
    const mermaidInput = input as { title?: string; code?: string };
    if (!mermaidInput.code) return null;
    return (
      <div className={className}>
        <MermaidToolOutput
          output={{ success: true, ...mermaidInput }}
          vizId={visualizationId}
          renderActions={combinedActions}
          onRetryError={onRetryError}
        />
      </div>
    );
  }

  if (toolName === 'molecule3d') {
    return (
      <div className={className}>
        <Molecule3DToolOutput output={{ success: true, ...input } as never} />
      </div>
    );
  }

  if (toolName === 'scene3d') {
    return (
      <div className={className}>
        <Scene3DToolOutput
          output={{ success: true, ...input } as never}
          displayContext={displayContext}
          visualizationId={visualizationId}
          onRetryError={onRetryError}
        />
      </div>
    );
  }

  if (toolName === 'p5') {
    return (
      <div className={className}>
        <P5ToolOutput
          output={{ success: true, ...input } as never}
          visualizationId={visualizationId}
          displayContext={displayContext}
          renderActions={combinedActions}
          onRetryError={onRetryError}
        />
      </div>
    );
  }

  if (toolName === 'remotion') {
    return (
      <div className={className}>
        <RemotionToolOutput
          output={{ success: true, ...input } as never}
          visualizationId={visualizationId}
          displayContext={displayContext}
          renderActions={combinedActions}
          onRetryError={onRetryError}
        />
      </div>
    );
  }

  if (toolName === 'hyperframes') {
    return (
      <div className={className}>
        <HyperframesToolOutput
          output={{ success: true, ...input } as never}
          visualizationId={visualizationId}
          displayContext={displayContext}
          renderActions={combinedActions}
        />
      </div>
    );
  }

  // Fast-tier scene-slot videos: the tool OUTPUT carries the deterministic
  // rendered document (the input only holds the scene slots), so render the
  // HyperFrames player from the output instead of the input.
  if (toolName === 'createSceneVideo') {
    const sceneOutput = output as { success?: boolean; htmlDocument?: string } | undefined;
    if (sceneOutput?.success && sceneOutput.htmlDocument) {
      return (
        <div className={className}>
          <HyperframesToolOutput
            output={{ success: true, ...(sceneOutput as object) } as never}
            visualizationId={visualizationId}
            displayContext={displayContext}
            renderActions={combinedActions}
          />
        </div>
      );
    }
    const pendingTitle = (input as { title?: string })?.title ?? 'Scene video';
    return (
      <div className={className}>
        <MediaPendingCard title="Assembling scenes…" meta={pendingTitle} aspect="video" />
      </div>
    );
  }

  if (toolName === 'htmlNative') {
    const htmlInput = input as { html?: string; title?: string } | undefined;
    const html = htmlInput?.html ?? '';
    const actions = (
      <>
        <NativeHtmlPrintButton title={htmlInput?.title} html={html} />
        {combinedActions}
      </>
    );
    return (
      <div className={className}>
        <div className="mb-1 flex justify-end">{actions}</div>
        <InlineHtmlBlock html={html} />
      </div>
    );
  }

  if (toolName === 'createNarratedVideo' || toolName === 'createParallelNarratedVideo') {
    const narratedOutput = output as {
      success?: boolean;
      taskId?: string;
      publicAccessToken?: string;
      title?: string;
      estimatedDurationSec?: number;
    } | undefined;
    if (narratedOutput?.taskId && narratedOutput?.publicAccessToken) {
      const inputTitle = (input as { title?: string })?.title;
      return (
        <div className={className}>
          <PendingNarratedVideo
            taskId={narratedOutput.taskId}
            publicAccessToken={narratedOutput.publicAccessToken}
            title={narratedOutput.title || inputTitle || 'Composing your video'}
            estimatedDurationSec={narratedOutput.estimatedDurationSec}
          />
        </div>
      );
    }
    // No output yet — input-streaming / input-available pending state.
    const pendingTitle = (input as { title?: string })?.title ?? 'Composing your video';
    return (
      <div className={className}>
        <MediaPendingCard title="Preparing video pipeline…" meta={pendingTitle} aspect="video" />
      </div>
    );
  }

  if (toolName === 'generateImageBatch') {
    return (
      <div className={className}>
        <div className="mb-1 flex justify-end">{combinedActions}</div>
        <GeneratedImageBatchToolOutput
          input={input as { items?: Array<{ label: string; prompt: string; model?: string }> }}
          output={output as never}
        />
      </div>
    );
  }

  if (
    toolName === 'generateVideoClip' ||
    toolName === 'extractVideoClip' ||
    toolName === 'captionVideoClip' ||
    toolName === 'createVideoRecut'
  ) {
    if (typeof output === 'object' && output && 'videoUrl' in output) {
      return (
        <div className={className}>
          <div className="mb-1 flex justify-end">{combinedActions}</div>
          <GeneratedVideoToolOutput output={output as never} />
        </div>
      );
    }
    const videoInput = input as { prompt?: string; model?: string };
    const pending: Record<string, { title: string; meta: string }> = {
      extractVideoClip: { title: 'Cutting clip…', meta: 'ffmpeg · seconds' },
      captionVideoClip: { title: 'Burning captions…', meta: 'ffmpeg · seconds' },
      createVideoRecut: { title: 'Assembling recut…', meta: 'ffmpeg · seconds' },
    };
    const p = pending[toolName] ?? {
      title: 'Rendering video…',
      meta: `${videoInput.model || DEFAULT_VIDEO_MODEL_ID} · 15s–4m`,
    };
    return (
      <div className={className}>
        <MediaPendingCard title={p.title} meta={p.meta} prompt={videoInput.prompt} aspect="video" />
      </div>
    );
  }

  if (toolName === 'generateImage') {
    if (typeof output === 'object' && output && 'images' in output) {
      return (
        <div className={className}>
          <div className="mb-1 flex justify-end">{combinedActions}</div>
          <GeneratedImageToolOutput output={output as never} />
        </div>
      );
    }

    // No output yet — render a real progress card. gpt-image-2 takes 60–150s
    // (5–10× slower than Nano Banana 2), so users need a visible signal that
    // the request is in flight, not stuck.
    const imageInput = input as { prompt?: string; model?: string };
    const isSlowModel = imageInput.model === 'gpt-image-2';
    return (
      <div className={className}>
        <MediaPendingCard
          title={isSlowModel ? 'Painting your image…' : 'Generating image…'}
          meta={`${imageInput.model || 'gemini-3.1-flash'}${isSlowModel ? ' · usually 1–2 min' : ' · usually 10–25s'}`}
          prompt={imageInput.prompt}
          aspect="image"
        />
      </div>
    );
  }

  if (MEDIA_ARTIFACT_TOOLS.has(toolName)) {
    return (
      <div className={cn(displayContext === 'artifact' && 'h-full min-h-0 w-full', className)}>
        <MediaArtifactRenderer
          input={input}
          output={output}
          displayContext={displayContext}
        />
      </div>
    );
  }

  if (DEV_ARTIFACT_TOOLS.has(toolName)) {
    return (
      <div className={cn(displayContext === 'artifact' && 'h-full min-h-0 w-full', className)}>
        <DevToolArtifactRenderer
          toolName={toolName}
          input={input}
          output={output}
          displayContext={displayContext}
        />
      </div>
    );
  }

  if (DOCUMENT_ARTIFACT_TOOLS.has(toolName)) {
    return (
      <div className={cn(displayContext === 'artifact' && 'h-full min-h-0 w-full', className)}>
        <DocumentArtifactRenderer
          input={input}
          output={output}
          displayContext={displayContext}
        />
      </div>
    );
  }

  if (
    toolName === 'createBrowser' ||
    toolName === 'browserLiveView' ||
    toolName === 'browserNavigate' ||
    toolName === 'browserScroll'
  ) {
    return (
      <div className={cn(displayContext === 'artifact' && 'h-full min-h-0 w-full', className)}>
        <BrowserLiveViewRenderer
          input={input}
          output={output}
          displayContext={displayContext}
          renderActions={displayContext === 'inline' ? renderActions : undefined}
        />
      </div>
    );
  }

  if (toolName === 'sandboxPreviewUrl') {
    return (
      <div className={cn(displayContext === 'artifact' && 'h-full min-h-0 w-full', className)}>
        <SandboxPreviewRenderer input={input} output={output} displayContext={displayContext} sessionId={sessionId} />
      </div>
    );
  }

  if (toolName === 'sandboxBrowserRun' && hasSandboxLiveView(output)) {
    return (
      <div className={cn(displayContext === 'artifact' && 'h-full min-h-0 w-full', className)}>
        <BrowserLiveView input={input} output={output} displayContext={displayContext} />
      </div>
    );
  }

  const toolToComponentMap: Record<string, keyof typeof visualizationRegistry> = {
    flowchart: 'Flowchart',
    timeline: 'Timeline',
    comparisonTable: 'ComparisonTable',
    conceptMap: 'ConceptMap',
    cycleDiagram: 'CycleDiagram',
    hierarchy: 'Hierarchy',
    graph: 'Graph',
    html: 'HtmlRenderer',
    svg: 'SvgRenderer',
  };

  const componentName = toolToComponentMap[toolName];
  if (!componentName) return null;

  const VisualizationComponent = visualizationRegistry[componentName];
  const visualizationProps = {
    ...input,
    renderActions: combinedActions,
    displayContext,
  };

  return (
    <div className={className}>
      <VisualizationComponent
        element={{
          props: visualizationProps as never,
          visualizationId,
        }}
      />
    </div>
  );
}
